/**
 * Promoter Login (WhatsApp OTP only)
 * ────────────────────────────────────────────────────────────────────────
 * Route: /account/promoter/login
 *
 * Promoters log in EXCLUSIVELY via a 6-digit code delivered on WhatsApp —
 * no password, no SMS fallback. Rationale:
 *   • We want a verified WhatsApp number on file for commission alerts +
 *     payout coordination.
 *   • Removes friction (no password to remember).
 *   • If a number isn't on WhatsApp, the user is steered to /promoters/apply
 *     instead of getting stuck at login.
 *
 * Backend endpoints used:
 *   POST /api/auth/send-otp-whatsapp        — WhatsApp-only OTP send
 *   POST /api/customer/auth/login-otp       — verify OTP + issue customer JWT
 *
 * On success, navigates to /account/promoter (or `?next=` if provided).
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../../App';
import { customerApi, publicApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Zap, MessageCircle, ArrowLeft, ShieldCheck, Loader2 } from 'lucide-react';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECS = 30;

export default function PromoterLoginPage() {
  const { loginCustomer } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get('next');
  const next = nextParam || location.state?.from?.pathname || '/account/promoter';

  const [phase, setPhase] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // ── cooldown tick ───────────────────────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // ── helpers ─────────────────────────────────────────────────────────────
  const phoneE164 = () => {
    const raw = phone.trim().replace(/\D/g, '');
    if (raw.length === 10) return `+91${raw}`;
    if (raw.length === 12 && raw.startsWith('91')) return `+${raw}`;
    return phone.startsWith('+') ? phone : `+91${raw}`;
  };

  const validPhone = () => /^\+91\d{10}$/.test(phoneE164());

  // ── actions ─────────────────────────────────────────────────────────────
  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!validPhone()) {
      toast.error('Enter a valid 10-digit WhatsApp number');
      return;
    }
    setSending(true);
    try {
      await publicApi.sendOTPWhatsApp(phoneE164());
      toast.success(`OTP sent on WhatsApp to ${phoneE164()}`);
      setPhase('otp');
      setCooldown(RESEND_COOLDOWN_SECS);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        "Couldn't send WhatsApp OTP. Make sure the number has WhatsApp installed.",
      );
    } finally {
      setSending(false);
    }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    await sendOtp();
  };

  const verifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      toast.error('Enter the 6-digit code from WhatsApp');
      return;
    }
    setVerifying(true);
    try {
      const res = await customerApi.loginOTP(phoneE164(), otp);
      loginCustomer(res.data.access_token, res.data.customer);
      toast.success(`Welcome back, ${res.data.customer.full_name || 'Promoter'}!`);
      navigate(next, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 404) {
        // No customer with this phone — push them to the apply flow
        toast.message('No promoter account found for this number.', {
          description: 'Apply to the promoter program to get started.',
          action: { label: 'Apply now', onClick: () => navigate('/promoters/apply') },
          duration: 8000,
        });
      } else {
        toast.error(detail || 'Could not verify OTP. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit when 6 digits are entered (keeps the flow fast)
  useEffect(() => {
    if (otp.length === OTP_LENGTH && !verifying) {
      verifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // ── UI ──────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="promoter-login-page"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0A0A0A' }}
    >
      {/* Hero top */}
      <div className="hero-gradient pb-6">
        <div className="max-w-[420px] mx-auto px-5 pt-10">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
              <Zap size={15} className="text-[#120F08]" />
            </div>
            <span className="font-['EB_Garamond'] text-[#F2F2F2] text-xl font-semibold">
              Scratch Unleashed
            </span>
          </div>
          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">
            Promoter sign-in
          </h1>
          <p className="text-[#B9B9B9] text-sm">
            One-tap login with a WhatsApp code. No password required.
          </p>
        </div>
      </div>

      <div className="flex-1 max-w-[420px] mx-auto w-full px-5 py-6">
        {/* ── Phase 1: phone ──────────────────────────────────────────── */}
        {phase === 'phone' && (
          <form onSubmit={sendOtp} className="space-y-4" data-testid="promoter-otp-step-phone">
            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <MessageCircle size={11} /> WhatsApp number
              </Label>
              <div className="flex gap-2">
                <span className="w-14 flex items-center justify-center bg-[#0F0F0F] border border-[#2A2A2A] rounded-md text-[#B9B9B9] text-sm">
                  +91
                </span>
                <Input
                  data-testid="promoter-phone-input"
                  inputMode="numeric"
                  placeholder="10-digit number"
                  value={phone.replace(/^\+91/, '')}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="flex-1 bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 placeholder:text-[#4A4A4A] h-12"
                  autoComplete="tel"
                  autoFocus
                />
              </div>
              <p className="text-[11px] text-[#7A7A72] mt-1">
                We&apos;ll send a one-time code on WhatsApp. The number must have WhatsApp installed.
              </p>
            </div>

            <Button
              type="submit"
              disabled={sending || !validPhone()}
              data-testid="promoter-send-otp-btn"
              className="w-full h-12 bg-[#D4A843] hover:bg-[#c19938] text-[#0A0A0A] font-semibold"
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <MessageCircle size={14} className="mr-2" />
                  Send WhatsApp code
                </>
              )}
            </Button>

            <div className="pt-4 text-center">
              <p className="text-[#7A7A72] text-xs">
                Not a promoter yet?{' '}
                <Link to="/promoters/apply" className="text-[#D4A843] hover:underline">
                  Apply to the program
                </Link>
              </p>
            </div>
          </form>
        )}

        {/* ── Phase 2: OTP ────────────────────────────────────────────── */}
        {phase === 'otp' && (
          <form onSubmit={verifyOtp} className="space-y-4" data-testid="promoter-otp-step-code">
            <button
              type="button"
              onClick={() => { setPhase('phone'); setOtp(''); }}
              className="inline-flex items-center gap-1 text-xs text-[#B9B9B9] hover:text-white mb-2"
              data-testid="promoter-otp-back-btn"
            >
              <ArrowLeft size={12} /> Change number
            </button>

            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <ShieldCheck size={11} /> Enter the 6-digit code sent to {phoneE164()}
              </Label>
              <Input
                data-testid="promoter-otp-input"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                placeholder="••••••"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                className="bg-[#111111] border-[#2A2A2A] focus-visible:ring-[#D4A843]/50 h-14 text-center text-2xl tracking-[0.6em] font-mono"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={verifying || otp.length !== OTP_LENGTH}
              data-testid="promoter-verify-otp-btn"
              className="w-full h-12 bg-[#D4A843] hover:bg-[#c19938] text-[#0A0A0A] font-semibold"
            >
              {verifying ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Verifying…
                </>
              ) : (
                'Sign in'
              )}
            </Button>

            <div className="pt-2 text-center">
              {cooldown > 0 ? (
                <p className="text-[#7A7A72] text-xs">
                  Didn&apos;t get it? Resend in {cooldown}s
                </p>
              ) : (
                <button
                  type="button"
                  onClick={resendOtp}
                  className="text-[#D4A843] hover:underline text-xs"
                  data-testid="promoter-resend-otp-btn"
                >
                  Resend WhatsApp code
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      <div className="max-w-[420px] mx-auto w-full px-5 pb-8">
        <p className="text-[10px] text-[#5A5A52] text-center leading-relaxed">
          By signing in, you agree to our{' '}
          <Link to="/terms-and-conditions" className="underline hover:text-[#B9B9B9]">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" className="underline hover:text-[#B9B9B9]">
            Privacy Policy
          </Link>.
        </p>
      </div>
    </div>
  );
}
